import { Colors } from '@amelio/ui-library';

import type {
    CompetencyTranslationChangeRequest,
    CompetencyTranslationGroup,
    ModifiedTranslation,
    TranslationFieldPath,
} from '@/types/competencies/CompetencyTranslation';
import type { User } from '@/types/directory/User';
import type { LocalizedText } from '@/utils/localization';

import { CompetencyTranslationFieldKind } from '@/constants/competencies/CompetencyTranslationFieldKind';
import { PROFICIENCY_LEVEL_ICON_OPTIONS } from '@/constants/competencies/ProficiencyLevels';
import { hasLocalizedText } from '@/utils/localization';

export type RuleAttributeKey =
    | 'jobTitleId'
    | 'departmentId'
    | 'companyAddressId'
    | 'employeeLevelId';
/** Look up the intrinsic color of a preset proficiency-level icon (falls back to the base text color). */
export const getProficiencyLevelIconColor = (icon: string): string =>
    PROFICIENCY_LEVEL_ICON_OPTIONS.find((option) => option.icon === icon)?.color ?? Colors.BASE;

export const getRuleAttributeValue = (user: User, attribute: RuleAttributeKey): string | null => {
    switch (attribute) {
        case 'jobTitleId':
            return user.jobTitleId;
        case 'departmentId':
            return user.departmentId;
        case 'companyAddressId':
            return user.companyAddressId;
        case 'employeeLevelId':
            return user.employeeLevelId;
        default:
            return null;
    }
};

export interface TranslationProgress {
    translated: number;
    total: number;
}

/** Counts how many translatable fields have a non-empty value in the given language. */
export const computeTranslationProgress = (
    groups: CompetencyTranslationGroup[],
    language: string,
): TranslationProgress => {
    let translated = 0;
    let total = 0;

    const count = (text: LocalizedText) => {
        if (!hasLocalizedText(text)) {
            return;
        }

        total += 1;
        if ((text[language] ?? '').trim().length > 0) {
            translated += 1;
        }
    };

    groups.forEach((group) => {
        count(group.name);
        count(group.description);
        group.competencies.forEach((competency) => {
            count(competency.name);
            count(competency.description ?? {});
            competency.questions.forEach((question) => {
                if (!hasLocalizedText(question.label)) {
                    return;
                }

                count(question.label);
                question.options.forEach((option) => count(option.label));
            });
        });
    });

    return { translated, total };
};

/** Returns only the fields (per language) whose value differs from the baseline. */
export const collectModifiedTranslations = (
    baseline: CompetencyTranslationGroup[],
    current: CompetencyTranslationGroup[],
): ModifiedTranslation[] => {
    const modified: ModifiedTranslation[] = [];

    const compare = (path: TranslationFieldPath, before: LocalizedText, after: LocalizedText) => {
        const languages = new Set([...Object.keys(before), ...Object.keys(after)]);
        languages.forEach((language) => {
            if ((before[language] ?? '') !== (after[language] ?? '')) {
                modified.push({ path, language, value: after[language] ?? '' });
            }
        });
    };

    current.forEach((group) => {
        const baseGroup = baseline.find((item) => item.id === group.id);
        if (!baseGroup) {
            return;
        }

        compare(
            { kind: CompetencyTranslationFieldKind.GROUP_NAME, groupId: group.id },
            baseGroup.name,
            group.name,
        );
        compare(
            { kind: CompetencyTranslationFieldKind.GROUP_DESCRIPTION, groupId: group.id },
            baseGroup.description,
            group.description,
        );

        group.competencies.forEach((competency) => {
            const baseCompetency = baseGroup.competencies.find((item) => item.id === competency.id);
            if (!baseCompetency) {
                return;
            }

            compare(
                {
                    kind: CompetencyTranslationFieldKind.COMPETENCY_NAME,
                    groupId: group.id,
                    competencyId: competency.id,
                },
                baseCompetency.name,
                competency.name,
            );

            if (
                hasLocalizedText(baseCompetency.description) &&
                hasLocalizedText(competency.description)
            ) {
                compare(
                    {
                        kind: CompetencyTranslationFieldKind.COMPETENCY_DESCRIPTION,
                        groupId: group.id,
                        competencyId: competency.id,
                    },
                    baseCompetency.description!,
                    competency.description!,
                );
            }

            competency.questions.forEach((question) => {
                if (!hasLocalizedText(question.label)) {
                    return;
                }

                const baseQuestion = baseCompetency.questions.find(
                    (item) => item.id === question.id,
                );
                if (!baseQuestion || !hasLocalizedText(baseQuestion.label)) {
                    return;
                }

                compare(
                    {
                        kind: CompetencyTranslationFieldKind.QUESTION_LABEL,
                        groupId: group.id,
                        competencyId: competency.id,
                        questionId: question.id,
                    },
                    baseQuestion.label,
                    question.label,
                );

                question.options.forEach((option) => {
                    if (!hasLocalizedText(option.label)) {
                        return;
                    }

                    const baseOption = baseQuestion.options.find((item) => item.id === option.id);
                    if (!baseOption || !hasLocalizedText(baseOption.label)) {
                        return;
                    }

                    compare(
                        {
                            kind: CompetencyTranslationFieldKind.OPTION_LABEL,
                            groupId: group.id,
                            competencyId: competency.id,
                            questionId: question.id,
                            optionId: option.id,
                        },
                        baseOption.label,
                        option.label,
                    );
                });
            });
        });
    });

    return modified;
};

/** Resolves the target entity id for a field path (its kind already matches the backend). */
const toTargetId = (path: TranslationFieldPath): string => {
    switch (path.kind) {
        case CompetencyTranslationFieldKind.GROUP_NAME:
        case CompetencyTranslationFieldKind.GROUP_DESCRIPTION:
            return path.groupId;
        case CompetencyTranslationFieldKind.COMPETENCY_NAME:
        case CompetencyTranslationFieldKind.COMPETENCY_DESCRIPTION:
            return path.competencyId;
        case CompetencyTranslationFieldKind.QUESTION_LABEL:
            return path.questionId;
        case CompetencyTranslationFieldKind.OPTION_LABEL:
            return path.optionId;
    }
};

/** Converts the frontend modified-translation list into the backend update request payload. */
export const toCompetencyTranslationChanges = (
    modified: ModifiedTranslation[],
): CompetencyTranslationChangeRequest[] =>
    modified.map(({ path, language, value }) => ({
        kind: path.kind,
        id: toTargetId(path),
        language,
        value,
    }));
